/**
 * ArBulkActionsMenu Component
 *
 * Dropdown menu for bulk operations on selected rows.
 * Shows when rows are selected, with a confirmation on every action a press
 * cannot reverse.
 *
 * Under the default `'visible'` scope the four row actions, the trigger's count
 * and the confirmation copy read `resolveSelectionScope`'s
 * `selectedVisibleRowIds` — the intersection with `uiState.selectableRowIds`, in
 * selectable order — never `selection.selectedRowIds` verbatim, so a row hidden
 * by a newly applied split, filter or view is not acted on (SYD-2386).
 *
 * Under `'all'`, reached only through `ArSelectionScopeBar`, the ticks stop
 * being the target: the actions send `{ allRows: true }` and the server
 * enumerates, so the count on the tag is `pagination.totalRows`. Duplicate is
 * the exception and goes dead with its reason — the callable caps a duplicate at
 * `AR_TABLE_PAGE_SIZE`, so there is no all-rows duplicate to offer. That
 * escalation is read through `isAllRows`, not off `selection.scope`: widening
 * the visible set retires it, so a cleared filter cannot leave a whole-table
 * delete armed behind five ticks.
 *
 * A `failed` result is silent here: `handleFirestoreWriteError` has already put
 * the reason on screen, a second toast would contradict it with a zero count,
 * and the selection is kept so the user can retry without re-selecting.
 *
 * The trigger itself is gated on the RAW selected count instead: a selection
 * narrowed entirely off the page still exists, and still arms ⌘⌫ and the
 * bulk-edit fan-out, so "Clear Selection" has to stay reachable. Only the four
 * destructive items go dead when there is nothing to act on.
 *
 * `duplicateRows`' count is documents created — a selected bundle header expands
 * server-side — so it can exceed the selected row count (SYD-2306).
 *
 * Delete asks nothing at any size (SYD-2447): it applies immediately and the undo
 * buttons carry the way back, so the only thing left to report a refusal is the
 * result message. It comes from `reportRowDeleteResult`, as every other delete's
 * does — this is the one path that also runs the `allRows` scope, where the server
 * refuses rows the client never named and a hard-coded "locked" would be a false
 * claim. Lock, unlock and duplicate keep their confirmations and their own copy —
 * none of them is reversible by a press.
 *
 * Copy Rows (SYD-2314) is the second item that goes dead under `isAllRows`, for
 * its own reason: the copy carries the rows themselves, and under that scope the
 * ticks stand for rows this client never loaded. Paste is not offered here —
 * it lands below one anchor row, which a selection does not name.
 *
 * Copy Rows passes the `'ticked'` scope explicitly. This menu is defined by the
 * ticks, so it must not inherit the focused row: an implicit fallback copied
 * whatever cell had focus and ignored the selection entirely.
 *
 * `modal` carries the action separately from `open` so the copy cannot flip
 * mid-close. The mount gate around the modal is a standing exception to the
 * no-gated-overlay rule and predates SYD-2386: without it, jsdom — which runs
 * no exit motion, so antd never hides the wrap — leaves a permanently visible
 * dialog over every later assertion. Lifting it needs a harness-level motion
 * setting, not a change here.
 */

import { FC, memo, useState, useContext } from 'react';
import { Button, Dropdown, Tag, Tooltip } from 'antd';
import {
  LockOutlined,
  UnlockOutlined,
  CopyOutlined,
  DeleteOutlined,
  CloseOutlined,
  DownOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useSnapshot } from 'valtio';
import { ArTableActionsContext, ArTableClipboardContext, ArTableUIStateContext } from '../context';
import { MessageContext } from '@/providers/MessageContext';
import { clearSelection } from '@/workflows/runtime/arTableSelection';
import { resolveSelectionScope } from '../selection';
import { BULK_CONFIRM_THRESHOLD } from '#types';
import { AR_TABLE_PAGE_SIZE } from '#types/System.Parameters.types';
import BulkConfirmationModal from './BulkConfirmationModal';
import { reportRowDeleteResult } from '../actions/reportRowDeleteResult';
import type { BulkActionType } from './BulkActionType.types';

type ArBulkActionsMenuProps = {
  disabled?: boolean;
};

const ArBulkActionsMenu: FC<ArBulkActionsMenuProps> = ({ disabled = false }) => {
  const { messageApi } = useContext(MessageContext);
  const uiState = useContext(ArTableUIStateContext)!;
  const actions = useContext(ArTableActionsContext)!;
  const { lockRows, deleteRows, duplicateRows } = actions;
  const { copyRowsToClipboard } = useContext(ArTableClipboardContext)!;
  const selectableSnap = useSnapshot(uiState.selectableRowIds);
  const selectionSnap = useSnapshot(uiState.selection);
  const paginationSnap = useSnapshot(uiState.pagination);

  const [modal, setModal] = useState<{ action: BulkActionType; open: boolean }>({
    action: 'lock',
    open: false,
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const selection = uiState.selection;
  const { selectedVisibleRowIds: targetRowIds, isAllRows } = resolveSelectionScope({
    selectableRowIds: selectableSnap,
    selectedRows: selectionSnap.selectedRows,
    scope: selectionSnap.scope,
  });
  /** What the action will touch — the ticks on screen, or the whole table. */
  const actionCount = isAllRows ? paginationSnap.totalRows : targetRowIds.length;
  const rowScope = isAllRows ? { allRows: true } as const : { rowIds: targetRowIds };
  const hasSelection = selectionSnap.selectedCount > 0;
  const nothingActionable = actionCount === 0;

  const isConfirming = (action: BulkActionType): boolean => modal.open && modal.action === action;
  const confirm = (action: BulkActionType): void => setModal({ action, open: true });
  const closeModal = (): void => setModal((current) => ({ ...current, open: false }));

  const handleClearSelection = () => {
    clearSelection(selection);
  };

  const handleLock = async () => {
    if (actionCount >= BULK_CONFIRM_THRESHOLD && !isConfirming('lock')) {
      confirm('lock');
      return;
    }
    closeModal();
    setIsProcessing(true);
    try {
      const result = await lockRows(rowScope, true);
      if (result.failed) return;
      if (result.skipped > 0) {
        messageApi.info(`Locked ${result.affected} rows. ${result.skipped} already locked.`);
      } else {
        messageApi.success(`Locked ${result.affected} rows.`);
      }
      handleClearSelection();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUnlock = async () => {
    if (actionCount >= BULK_CONFIRM_THRESHOLD && !isConfirming('unlock')) {
      confirm('unlock');
      return;
    }
    closeModal();
    setIsProcessing(true);
    try {
      const result = await lockRows(rowScope, false);
      if (result.failed) return;
      if (result.skipped > 0) {
        messageApi.info(`Unlocked ${result.affected} rows. ${result.skipped} already unlocked.`);
      } else {
        messageApi.success(`Unlocked ${result.affected} rows.`);
      }
      handleClearSelection();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDuplicate = async () => {
    if (actionCount >= BULK_CONFIRM_THRESHOLD && !isConfirming('duplicate')) {
      confirm('duplicate');
      return;
    }
    closeModal();
    setIsProcessing(true);
    try {
      const result = await duplicateRows(targetRowIds);
      if (result.failed) return;
      messageApi.success(`Duplicated ${result.affected} rows.`);
      handleClearSelection();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    setIsProcessing(true);
    try {
      uiState.cellEditing.editingCellsByKey = {};
      uiState.bulkEditPreview.byColumnId = {};
      const result = await deleteRows(rowScope);
      if (result.failed) return;
      reportRowDeleteResult(messageApi, result, isAllRows ? { allRows: true } : targetRowIds);
      handleClearSelection();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleModalOk = async () => {
    switch (modal.action) {
      case 'lock':
        await handleLock();
        break;
      case 'unlock':
        await handleUnlock();
        break;
      case 'duplicate':
        await handleDuplicate();
        break;
    }
  };

  const rowNoun = actionCount === 1 ? 'Row' : 'Rows';

  const items: MenuProps['items'] = [
    {
      key: 'lock',
      label: `Lock ${rowNoun}`,
      icon: <LockOutlined />,
      onClick: handleLock,
      disabled: disabled || isProcessing || nothingActionable,
    },
    {
      key: 'unlock',
      label: `Unlock ${rowNoun}`,
      icon: <UnlockOutlined />,
      onClick: handleUnlock,
      disabled: disabled || isProcessing || nothingActionable,
    },
    { type: 'divider' as const },
    {
      key: 'copy-rows',
      label: isAllRows ? (
        <Tooltip title="Copy takes the rows you ticked. Tick the rows you want, then copy.">
          <span aria-label="artable-copy-rows-all-rows-reason">{`Copy ${rowNoun}`}</span>
        </Tooltip>
      ) : `Copy ${rowNoun}`,
      icon: <CopyOutlined />,
      onClick: () => void copyRowsToClipboard('ticked'),
      disabled: disabled || isProcessing || nothingActionable || isAllRows,
    },
    { type: 'divider' as const },
    {
      key: 'duplicate',
      label: isAllRows ? (
        <Tooltip title={`Duplicate handles up to ${AR_TABLE_PAGE_SIZE} rows. Select visible rows only to duplicate.`}>
          <span aria-label="artable-duplicate-all-rows-reason">{`Duplicate ${rowNoun}`}</span>
        </Tooltip>
      ) : `Duplicate ${rowNoun}`,
      icon: <CopyOutlined />,
      onClick: handleDuplicate,
      disabled: disabled || isProcessing || nothingActionable || isAllRows,
    },
    { type: 'divider' as const },
    {
      key: 'delete',
      label: `Delete ${rowNoun}`,
      icon: <DeleteOutlined />,
      onClick: handleDelete,
      disabled: disabled || isProcessing || nothingActionable,
      danger: true,
    },
    { type: 'divider' as const },
    {
      key: 'deselect',
      label: 'Clear Selection',
      icon: <CloseOutlined />,
      onClick: handleClearSelection,
      disabled: disabled || isProcessing,
    },
  ];

  return (
    <>
      <Dropdown
        menu={{ items }}
        trigger={['click']}
        disabled={disabled || isProcessing}
      >
        <Button
          aria-label="artable-bulk-actions-button"
          type="link"
          size="small"
          loading={isProcessing}
          disabled={!hasSelection}
        >
          Actions<DownOutlined /><Tag style={{ borderRadius: 20 }}>{actionCount.toLocaleString()}</Tag>
        </Button>
      </Dropdown>

      {modal.open && (
        <BulkConfirmationModal
          action={modal.action}
          selectedCount={actionCount}
          scope={selectionSnap.scope}
          open={modal.open}
          confirmLoading={isProcessing}
          onConfirm={() => {
            void handleModalOk();
          }}
          onCancel={closeModal}
        />
      )}
    </>
  );
};

export default memo(ArBulkActionsMenu);
